import { Report, ReportFile, ReportProperty, ReportSection } from '../../common/types/reports';
import { ReportFullStepList } from '../types';
import { useEffect, useState } from 'react';
import { useUpdateReport } from '../../common/components/mutations';

// mark file as uploaded to be able to work with it in ReportBuilder
export const UPLOADED_DOC_LABEL = 'uploaded-document';

export const UPLOADED_LOGO_LABEL = 'uploaded-logo';

export interface UseReportUpdaterResult {
  /**
   * Save changes to the report
   */
  saveChanges: () => Promise<void>;
  /**
   * Get section property value
   */
  getPropertyValue: (key: string) => string;
  /**
   * Set section property value
   */
  setPropertyValue: (key: string, value: string) => void;
  /**
   * Add file to the section
   * @param fileUrl File url
   * @param fileUploadName File name
   * @param fileLabel File label
   * @param replaceFileByLabelName @default false If true, file with the same label will be replaced
   * @param isReportLevel @default false If true, file will be added to the report level
   */
  addFile: (
    fileUrl: string,
    fileUploadName?: string,
    fileLabel?: string,
    replaceFileByLabelName?: boolean,
    isReportLevel?: boolean,
  ) => void;
  /**
   * Remove file from the section
   */
  removeFile: (id: number) => void;
  /**
   * Section data to work with
   */
  sectionData?: ReportSection;
  /**
   * Is report saving in progress
   */
  isLoading: boolean;
  /**
   * Report data to work with
   */
  reportData: Report;
  /**
   * Callback to update report data (the same way as useState does)
   */
  setReportData: (report: Report) => void;
}

interface UseReportUpdaterParameters {
  /**
   * Report to work with
   */
  report: Report;
  /**
   * Name of the section to work with
   */
  sectionName: ReportFullStepList;
}

/**
 * Hook to work with report data (update sections property, save changes, etc)
 */
export const useReportUpdater = ({
  report,
  sectionName: activeSectionName,
}: UseReportUpdaterParameters): UseReportUpdaterResult => {
  const { mutateAsync, isPending: isLoading } = useUpdateReport();
  const [reportData, setReportData] = useState(report);
  const sectionData = reportData.sections.find(
    ({ sectionName }) => sectionName === activeSectionName,
  );

  // If report was changed from outside, update reportData
  useEffect(() => {
    setReportData(report);
  }, [report]);

  /**
   * Callback to update report section data
   * @param updatedSectionData Updated section data
   */
  const setSectionData = (updatedSectionData: ReportSection): void => {
    if (!reportData) {
      return;
    }

    const updatedSections = [...reportData.sections];
    const sectionIndex = updatedSections.findIndex(
      ({ sectionName }) => sectionName === activeSectionName,
    );

    if (sectionIndex === -1) {
      return;
    }

    updatedSections[sectionIndex] = {
      ...updatedSectionData,
    };

    setReportData({
      ...reportData,
      sections: updatedSections,
    });
  };

  /**
   * Section property getter
   */
  const getPropertyValue = (key: string): string => {
    return (
      sectionData?.properties.find(({ propertyLabel }) => propertyLabel === key)?.propertyValue ??
      ''
    );
  };

  /**
   * Section property setter
   */
  const setPropertyValue = (key: string, value: string): void => {
    if (!sectionData) {
      return;
    }

    const updatedProperties = [...sectionData.properties];
    const propertyToUpdateIndex = updatedProperties.findIndex(
      ({ propertyLabel }) => propertyLabel === key,
    );
    if (propertyToUpdateIndex >= 0) {
      updatedProperties[propertyToUpdateIndex] = {
        ...updatedProperties[propertyToUpdateIndex],
        propertyValue: value,
      };
    } else {
      updatedProperties.push({
        propertyLabel: key,
        propertyValue: value,
        reportID: report.id,
        sectionID: sectionData.sectionID,
        propertySectionID: sectionData.sectionID,
      } as ReportProperty);
    }

    setSectionData({
      ...sectionData,
      properties: updatedProperties,
    });
  };

  /**
   * Add file to the section
   */
  const addFile = (
    fileUrl: string,
    fileUploadName?: string,
    fileLabel: string = UPLOADED_DOC_LABEL,
    replaceFileByLabelName = false,
    isReportLevel = false,
  ): void => {
    if (!sectionData) {
      return;
    }

    const updatedFiles = [...(isReportLevel ? reportData.files : sectionData.files)];
    const newFile: ReportFile = {
      id: 0,
      fileID: 0,
      fileStateID: 1,
      fileTypeID: 1,
      fileUrl,
      fileUploadName,
      fileLabel,
      sectionID: sectionData.sectionID,
    } as ReportFile;

    let fileToUpdateIndex = -1;
    if (replaceFileByLabelName) {
      fileToUpdateIndex = updatedFiles.findIndex(({ fileLabel }) => fileLabel === fileLabel);
    }

    if (fileToUpdateIndex >= 0) {
      updatedFiles[fileToUpdateIndex] = newFile;
    } else {
      updatedFiles.push(newFile);
    }

    if (isReportLevel) {
      setReportData({
        ...reportData,
        files: updatedFiles,
      });
    } else {
      setSectionData({
        ...sectionData,
        files: updatedFiles,
      });
    }
  };

  /**
   * Remove file from section
   */
  const removeFile = (fileIdToDelete: number): void => {
    if (!sectionData) {
      return;
    }

    const updatedFiles = sectionData.files.filter(({ id }) => id !== fileIdToDelete);
    setSectionData({
      ...sectionData,
      files: updatedFiles,
    });
  };

  const saveChanges = async (): Promise<void> => {
    await mutateAsync(reportData);
  };

  return {
    addFile,
    removeFile,
    saveChanges,
    getPropertyValue,
    setPropertyValue,
    setReportData,
    reportData,
    sectionData,
    isLoading,
  };
};
